import clsx from "clsx";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CHIP_TONE, type ChipKind } from "./chipTone";

interface TermChipProps {
  kind: ChipKind;
  label: string;
  /** A watchlist's highlight colour. */
  dot?: string | null;
  /** Hover text: what a list chip holds, or a company for a ticker. */
  title?: string;
  /** Omit for a read-only chip (the concierge preview). */
  onRemove?: () => void;
  /** Flags a chip that can't be saved as it is, like an AND list. */
  warning?: boolean;
}

/** One entry in a builder box, coloured by what it is (chipTone.ts). */
export default function TermChip({ kind, label, dot, title, onRemove, warning }: TermChipProps) {
  return (
    <span
      title={title}
      className={clsx(
        "inline-flex h-[26px] max-w-full items-center gap-1.5 whitespace-nowrap rounded border text-[13px] leading-none",
        onRemove ? "pl-2 pr-0.5" : "px-2",
        warning ? "border-dashed border-amber-500/75 bg-transparent text-amber-200" : CHIP_TONE[kind]
      )}
    >
      {dot && (
        <span
          aria-hidden="true"
          className="size-2 shrink-0 rounded-full"
          style={{ backgroundColor: dot }}
        />
      )}
      <span className={clsx("truncate", kind === "ticker" && "font-semibold")}>{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${label}`}
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-sm border-0 bg-transparent p-0 text-current opacity-75 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
        >
          <XMarkIcon className="size-3" strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

/**
 * An entry held back for a check: the word as typed, dashed, with a "?".
 * Opens its check panel below the box.
 */
export function FlagChip({
  word,
  open,
  onClick,
}: {
  word: string;
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-label={`Check “${word}”`}
      className={clsx(
        "inline-flex h-[26px] items-center gap-1 whitespace-nowrap rounded border border-dashed border-amber-500/75 px-2 py-0 text-[13px] font-normal leading-none text-amber-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70",
        open ? "bg-amber-500/10" : "bg-transparent hover:bg-amber-500/5"
      )}
    >
      {word}
      <span aria-hidden="true" className="font-bold">
        ?
      </span>
    </button>
  );
}

/**
 * The question for one held-back entry, under its box: what it looks like,
 * and the ways to settle it. Every way keeps the word — nothing is dropped
 * unless the user removes it.
 */
export function CheckPanel({
  message,
  actions,
}: {
  message: string;
  actions: { label: string; onClick: () => void; primary?: boolean }[];
}) {
  return (
    <div
      role="group"
      aria-label={message}
      className="flex flex-wrap items-center gap-2 rounded border border-amber-500/35 bg-amber-500/5 py-1.5 pl-3 pr-2 text-[13px] text-gray-200"
    >
      <span className="min-w-[220px] flex-1">{message}</span>
      {actions.map((action) => (
        <button
          key={action.label}
          type="button"
          onClick={action.onClick}
          className={clsx(
            "h-[26px] rounded px-2.5 py-0 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70",
            action.primary
              ? "border border-gray-600 bg-gray-800 font-medium text-gray-100 hover:border-gray-500 hover:bg-gray-700"
              : "border-0 bg-transparent font-normal text-gray-300 hover:bg-gray-800 hover:text-gray-100"
          )}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}
